import {
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  PersonOff as PersonOffIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import type { FieldErrors } from "react-hook-form";

import type { InsuranceVerificationFormFields } from "../form/validation.zod";
import type { AppliedRuleDisplay } from "../utils/agentRules";
import VerificationForm from "./VerificationForm";

interface CarrierContactProps {
  verification: InsuranceVerificationMessage;
  onCarrierContactChange: (outcome: CarrierContact) => void;
  selectedCarrierContact: CarrierContact | null;
  onPredictedOutcomeChange?: (outcome: string) => void;
  onFormDataChange?: (data: InsuranceVerificationDetailUpdate) => void;
  onValidationChange?: (
    errors: FieldErrors<InsuranceVerificationFormFields>,
    warnings: string[],
  ) => void;
  onAppliedRulesChange?: (rules: AppliedRuleDisplay[]) => void;
}

export default function CarrierContact({
  verification,
  onCarrierContactChange,
  selectedCarrierContact,
  onPredictedOutcomeChange,
  onFormDataChange,
  onValidationChange,
  onAppliedRulesChange,
}: CarrierContactProps) {
  const [showDetailsForm, setShowDetailsForm] = useState(false);

  useEffect(() => {
    if (selectedCarrierContact === "CARRIER_AVAILABLE" && verification) {
      setShowDetailsForm(true);
    } else {
      setShowDetailsForm(false);
    }
  }, [selectedCarrierContact, verification]);

  const contactOptions = [
    {
      value: "CARRIER_AVAILABLE" as CarrierContact,
      label: "Available",
      icon: <CheckCircleIcon />,
      color: "success" as const,
      testId: "carrierAvailable",
    },
    {
      value: "PATIENT_NOT_FOUND" as CarrierContact,
      label: "Patient Not Found",
      icon: <PersonOffIcon />,
      color: "warning" as const,
      testId: "patientNotFound",
    },
    {
      value: "CARRIER_UNAVAILABLE" as CarrierContact,
      label: "Carrier Unavailable",
      icon: <ErrorOutlineIcon />,
      color: "error" as const,
      testId: "carrierUnavailable",
    },
  ];

  const selectedOption = contactOptions.find(
    (option) => option.value === selectedCarrierContact,
  );

  const bennyFlaggedPatientNotFound =
    verification.verificationStatus !== "COMPLETED" &&
    verification.verificationOutcome === "PATIENT_NOT_FOUND";

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <PhoneIcon color="primary" />
        <Typography variant="h6">Carrier Contact</Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Before proceeding with verification details, confirm whether you were
        able to reach the insurance carrier and locate the patient in their
        system.
      </Alert>

      {bennyFlaggedPatientNotFound && (
        <Alert
          severity="info"
          sx={{
            mb: 3,
            backgroundColor: "#e3f2fd",
            border: "1px solid #bbdefb",
            "& .MuiAlert-icon": {
              color: "#1976d2",
            },
          }}
        >
          <Typography variant="body2">
            <strong>Benny&apos;s recommendation:</strong> &quot;Patient Not
            Found&quot; - I wasn&apos;t able to locate this patient with the
            carrier - please confirm below.
          </Typography>
        </Alert>
      )}

      <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
        <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
          Were you able to reach the insurance carrier and find the patient?
        </FormLabel>

        <RadioGroup
          value={selectedCarrierContact}
          onChange={(e) => {
            const value = e.target.value as CarrierContact;
            onCarrierContactChange(value);
          }}
        >
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {contactOptions.map((option) => (
              <Card
                key={option.value}
                variant="outlined"
                data-testid={option.testId}
                sx={{
                  flex: 1,
                  minWidth: 200,
                  border: selectedCarrierContact === option.value ? 2 : 1,
                  borderColor:
                    selectedCarrierContact === option.value
                      ? `${option.color}.main`
                      : "divider",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": {
                    boxShadow: 1,
                  },
                }}
                onClick={() => onCarrierContactChange(option.value)}
              >
                <CardContent sx={{ p: 2 }}>
                  <FormControlLabel
                    value={option.value}
                    control={<Radio sx={{ display: "none" }} />}
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            color: `${option.color}.main`,
                            display: "flex",
                            alignItems: "center",
                            minWidth: 20,
                          }}
                        >
                          {option.icon}
                        </Box>
                        <Box sx={{ textAlign: "left" }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              lineHeight: 1.2,
                            }}
                          >
                            {option.label}
                          </Typography>
                        </Box>
                      </Box>
                    }
                    sx={{ m: 0, width: "100%" }}
                  />
                </CardContent>
              </Card>
            ))}
          </Box>
        </RadioGroup>
      </FormControl>

      {selectedOption && (
        <Alert severity={selectedOption.color} sx={{ mb: 3 }}>
          <Typography variant="body2">
            {(() => {
              if (selectedOption.value === "CARRIER_AVAILABLE") {
                return "Great! Please complete the verification details below.";
              }
              if (selectedOption.value === "PATIENT_NOT_FOUND") {
                return "The verification will be marked as completed with 'Patient Not Found' outcome.";
              }
              return "The verification will be marked as completed with 'Carrier Unavailable' outcome.";
            })()}
          </Typography>
        </Alert>
      )}

      {showDetailsForm && verification && (
        <Box sx={{ mt: 3 }}>
          <VerificationForm
            verification={verification}
            onPredictedOutcomeChange={onPredictedOutcomeChange}
            onFormDataChange={onFormDataChange}
            onValidationChange={onValidationChange}
            onAppliedRulesChange={onAppliedRulesChange}
          />
        </Box>
      )}
    </Box>
  );
}
